import React, { useMemo } from "react";
import { View, StyleSheet, Alert } from "react-native";
import { useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { CircleOff } from "lucide-react-native";
import {
  QuestionLayout,
  SquareOptionButton,
} from "@src/packages/medical-history/components";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useTranslations } from "@src/packages/i18n";
import { useContraception } from "../ContraceptionContext";
import type { ContraceptionDetail } from "../ContraceptionContext";
import type { ContraceptionStackParamList } from "../ContraceptionNavigator";
import { CONTRACEPTION_METHOD_OPTIONS } from "../options";
import type { ContraceptionMethod } from "../options";

export function ContraceptionMethodsScreen() {
  const t = useTranslations();
  const { state, updateState, save } = useContraception();
  const navigation =
    useNavigation<NativeStackNavigationProp<ContraceptionStackParamList>>();
  const { data: patient } = usePatient();
  const patientSex = patient?.gender;

  const visibleOptions = useMemo(
    () =>
      CONTRACEPTION_METHOD_OPTIONS.filter(
        (o) => o.sex === "both" || !patientSex || o.sex === patientSex,
      ),
    [patientSex],
  );

  const selectedMethods = useMemo(
    () => new Set(state.contraceptions.map((c) => c.method)),
    [state.contraceptions],
  );

  const isNone = state.usesContraception === false;

  const toggleMethod = (method: ContraceptionMethod) => {
    if (selectedMethods.has(method)) {
      const removedIds = state.contraceptions
        .filter((c) => c.method === method && c.id)
        .map((c) => c.id!);
      const remaining = state.contraceptions.filter((c) => c.method !== method);
      updateState({
        usesContraception: remaining.length > 0 ? true : null,
        contraceptions: remaining,
        contraceptionIdsToDelete: [
          ...state.contraceptionIdsToDelete,
          ...removedIds,
        ],
      });
    } else {
      // Add
      const newEntry: ContraceptionDetail = { method };
      updateState({
        usesContraception: true,
        contraceptions: [...state.contraceptions, newEntry],
      });
    }
  };

  const handleNone = () => {
    const idsToDelete = state.contraceptions
      .filter((c) => c.id)
      .map((c) => c.id!);
    updateState({
      usesContraception: false,
      contraceptions: [],
      contraceptionIdsToDelete: [
        ...state.contraceptionIdsToDelete,
        ...idsToDelete,
      ],
    });
  };

  const handleSave = async () => {
    try {
      await save();
      if (isNone) {
        navigation.navigate("ContraceptionComplete");
      } else {
        navigation.navigate("ContraceptionDetails");
      }
    } catch {
      Alert.alert(
        t("med_history.save_failed_title"),
        t("med_history.save_failed_message"),
        [{ text: t("med_history.save_failed_ok") }],
      );
    }
  };

  const isValid =
    isNone ||
    (state.usesContraception === true && state.contraceptions.length > 0);

  return (
    <QuestionLayout
      title={t("med_history.gynae.contraception.methods_title")}
      subtitle={t("med_history.gynae.contraception.methods_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!isValid}
      saveLabel={t("med_history.gynae.contraception.continue")}
    >
      <View style={styles.container}>
        <View style={styles.grid}>
          {visibleOptions.map((option) => (
            <View key={option.id} style={styles.cell}>
              <SquareOptionButton
                icon={option.icon}
                label={t(option.labelKey)}
                isSelected={!isNone && selectedMethods.has(option.id)}
                onPress={() => {
                  if (isNone) {
                    updateState({
                      usesContraception: true,
                      contraceptions: [{ method: option.id }],
                    });
                  } else {
                    toggleMethod(option.id);
                  }
                }}
                labelSize="sm"
              />
            </View>
          ))}
          <View style={styles.cell}>
            <SquareOptionButton
              icon={CircleOff}
              label={t("med_history.gynae.contraception.none")}
              isSelected={isNone}
              onPress={handleNone}
              labelSize="sm"
            />
          </View>
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    marginBottom: 40,
  },
  grid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    width: "100%",
  },
  cell: {
    width: "47%",
  },
});
